import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SiteNav } from './SiteNav';
import { Check, Download, Mail, LogOut, ArrowRight, Calendar, Clock, Search, Ruler, ChevronDown, ChevronUp, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { QSPriceBadge } from './QSPriceBadge';
import { QSCallCTA } from './QSCallCTA';
import { useAuth } from '../contexts/AuthContext';
import './ClientPortal.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_LABELS = {
  indicative: 'Indicative estimate',
  detailed: 'Full Project Pack',
  scope_materials: 'Materials Schedule',
  scope_sow: 'Schedule of Works',
  scope_full: 'Full Project Pack',
  qs_check: 'QS Check',
  managed: 'Managed QS Service',
};

const PRODUCT_ICONS = {
  indicative: '📋', detailed: '📐',
  scope_materials: '📋', scope_sow: '📐', scope_full: '📦',
  qs_check: '🔍', managed: '🏢',
};

const QS_STATUS_STYLES = {
  'Ordered': { bg: '#FEF3C7', text: '#92400E' },
  'In Review': { bg: '#DBEAFE', text: '#1E40AF' },
  'Report Ready': { bg: '#DCFCE7', text: '#166534' },
};

export const ClientPortal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, resetPassword } = useAuth();

  const email = user && user.email;
  const authenticated = !!user;
  const authChecking = user === null;

  // Portal data
  const [orders, setOrders] = useState([]);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Phase B — drafts
  const [drafts, setDrafts] = useState([]);
  const [sessionInfo, setSessionInfo] = useState(null); // { session_count, session_limit, tier }

  // Phase C — billing
  const [billing, setBilling] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchBilling = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/billing/me`);
      setBilling(res.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Fetch billing error:', err?.message);
      }
    }
  }, []);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/billing/portal`);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not open billing portal');
      setPortalLoading(false);
    }
  };

  const [qsRedeemLoading, setQsRedeemLoading] = useState(false);
  const handleRedeemQsConsultation = async () => {
    setQsRedeemLoading(true);
    try {
      const res = await axios.post(`${API}/billing/qs-call-redeem`, {});
      if (res.data?.calendly_url) {
        toast.success('Project Cost Review redeemed — opening your booking link.');
        window.open(res.data.calendly_url, '_blank', 'noopener');
        fetchBilling();
      } else {
        toast.error('Redemption succeeded but no booking link returned. Check your email.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Redemption failed');
    } finally {
      setQsRedeemLoading(false);
    }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client/orders`);
      setOrders(res.data.orders || []);
      setMarketingConsent(res.data.marketing_consent || false);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Fetch orders error:', err?.message);
      }
      setOrders([]);
    }
    setLoading(false);
  }, []);

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scope/drafts`);
      setDrafts(res.data.drafts || []);
      setSessionInfo({
        count: res.data.session_count || 0,
        limit: res.data.session_limit || 2,
        tier: res.data.tier || 'free',
      });
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Fetch drafts error:', err?.message);
      }
    }
  }, []);

  // Legacy-link support: if the URL carries an old ?token=..., swap it for the
  // new /reset-password page (same underlying token collection works).
  useEffect(() => {
    const legacyToken = searchParams.get('token');
    if (legacyToken) {
      navigate(`/reset-password?token=${encodeURIComponent(legacyToken)}`, { replace: true });
      return;
    }
    // Redirect to /login once auth check completes and user is unauthenticated
    if (user === false) {
      navigate('/login', { replace: true });
      return;
    }
    if (user) {
      fetchOrders();
      fetchDrafts();
      fetchBilling();
    }
  }, [user, searchParams, navigate, fetchOrders, fetchDrafts, fetchBilling]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleDownload = async (orderRef, type) => {
    try {
      const res = await axios.get(`${API}/scope/download?ref=${encodeURIComponent(orderRef)}&type=${type}`);
      if (res.data.url) window.open(res.data.url, '_blank');
      else alert('Document not available yet.');
    } catch (err) {
      console.error('Download error:', err?.message);
      alert('Download not available. Documents may still be generating.');
    }
  };

  const handleShareSow = async (orderRef) => {
    try {
      const res = await axios.post(`${API}/scope/share-sow`, { ref: orderRef });
      if (res.data?.share_url) {
        try { await navigator.clipboard.writeText(res.data.share_url); } catch (_) {}
        const when = res.data.expires_at
          ? new Date(res.data.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : '30 days';
        toast.success(`Share link copied to your clipboard — expires ${when}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create share link');
    }
  };

  const handleToggleConsent = async () => {
    const newVal = !marketingConsent;
    setMarketingConsent(newVal);
    try {
      await axios.post(`${API}/client/marketing-consent`, { consented: newVal });
    } catch (err) {
      console.error('Consent update error:', err?.message);
      setMarketingConsent(!newVal);
    }
  };

  const handleAddQSCheck = async (order) => {
    try {
      const res = await axios.post(`${API}/scope/checkout`, {
        product_type: 'qs_check', email, address: order.address || order.address_full || '',
        linked_ref: order.order_ref,
      });
      window.location.href = res.data.url;
    } catch (err) { console.error('Checkout error:', err?.message); alert('Unable to start checkout.'); }
  };

  const handleContinueDraft = (draftId) => {
    navigate(`/scope?draft=${encodeURIComponent(draftId)}`);
  };
  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/scope/drafts/${draftId}`);
      fetchDrafts();
    } catch (err) { console.error('Delete error:', err?.message); }
  };
  const handleArchiveDraft = async (draftId) => {
    try {
      await axios.post(`${API}/scope/drafts/${draftId}/archive`);
      fetchDrafts();
    } catch (err) { console.error('Archive error:', err?.message); }
  };
  const handleArchiveProject = async (projectId) => {
    if (!window.confirm('Archive this project? It stays downloadable but frees a session slot.')) return;
    try {
      await axios.post(`${API}/scope/projects/archive`, { project_id: projectId });
      fetchOrders();
      fetchDrafts();
    } catch (err) { console.error('Archive project error:', err?.message); }
  };

  const handleDeleteProject = async (projectId, address) => {
    const first = window.confirm(
      `Delete "${address || 'this project'}" permanently?\n\n` +
      'This removes the project record and any saved drafts from your account. ' +
      'Paid downloads already in your inbox are unaffected. This cannot be undone.'
    );
    if (!first) return;
    const confirm2 = window.prompt('Type DELETE in capitals to confirm:');
    if (confirm2 !== 'DELETE') return;
    try {
      await axios.delete(`${API}/client/projects/${projectId}`);
      fetchOrders();
      fetchDrafts();
    } catch (err) {
      console.error('Delete project error:', err?.message);
      alert('Delete failed. Please try again or contact support.');
    }
  };

  // ── While auth is resolving or redirecting: render a loading shell ──
  if (authChecking || !authenticated) {
    return (
      <div className="cp-app" data-testid="client-portal-loading">
        <SiteNav cta="estimate" />
        <div className="cp-login-wrap">
          <div className="cp-login-card">
            <div className="cp-login-icon"><Mail className="w-6 h-6" style={{ color: '#2563EB' }} /></div>
            <h2 className="cp-login-title">Loading your account…</h2>
            <p className="cp-login-sub">One moment.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── NOT AUTHENTICATED: handled above by redirect to /login — nothing to render here ──

  // ── LOADING ──
  if (loading) {
    return (
      <div className="cp-app">
        <SiteNav cta="estimate" />
        <div className="cp-loading">
          <div className="cp-dots"><div /><div /><div /></div>
          <div>Loading your account...</div>
        </div>
      </div>
    );
  }

  // ── AUTHENTICATED: Portal ──
  return (
    <div className="cp-app" data-testid="client-portal-auth">
      <SiteNav cta="estimate" />
      <div className="cp-portal">
        {/* Header */}
        <div className="cp-portal-hdr">
          <div>
            <h1 className="cp-portal-title">Your Account</h1>
            <div className="cp-portal-email">{email}</div>
          </div>
          <button className="cp-logout" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>

        {/* Phase C — Subscription / allowance summary */}
        {billing && (
          <div
            data-testid="billing-summary"
            style={{
              background: billing.tier === 'free' ? '#F1F5F9' : '#0F2742',
              color: billing.tier === 'free' ? '#0F2742' : '#fff',
              padding: '16px 18px', borderRadius: 12, marginBottom: 18,
              display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
              alignItems: 'center', gap: 14,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em',
                              textTransform: 'uppercase', opacity: 0.75 }}>
                  Plan
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}
                     data-testid="tier-badge">
                  {billing.tier === 'pro_plus' ? 'Pro+' :
                   billing.tier === 'pro' ? 'Pro' : 'Free'}
                </div>
              </div>
              {billing.tier !== 'free' && (
                <>
                  <div style={{ borderLeft: '1px solid rgba(255,255,255,.22)', height: 28 }} />
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em',
                                  textTransform: 'uppercase', opacity: 0.75 }}>Downloads</div>
                    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}
                         data-testid="downloads-remaining">
                      {billing.monthly_allowances.downloads_remaining} of {billing.monthly_allowances.downloads_cap} remaining
                    </div>
                  </div>
                  {billing.tier === 'pro_plus' && (
                    <>
                      <div style={{ borderLeft: '1px solid rgba(255,255,255,.22)', height: 28 }} />
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em',
                                      textTransform: 'uppercase', opacity: 0.75 }}>Project Cost Reviews</div>
                        <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}
                             data-testid="qs-calls-remaining">
                          {billing.monthly_allowances.quarterly_calls_remaining ?? billing.monthly_allowances.qs_calls_remaining} of {billing.monthly_allowances.quarterly_calls_cap ?? billing.monthly_allowances.qs_calls_cap} per year
                        </div>
                      </div>
                    </>
                  )}
                  {billing.monthly_allowances.reset_on && (
                    <>
                      <div style={{ borderLeft: '1px solid rgba(255,255,255,.22)', height: 28 }} />
                      <div style={{ fontSize: 11, opacity: 0.8 }}>
                        Resets {new Date(billing.monthly_allowances.reset_on).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {billing.tier === 'free' ? (
                <button
                  onClick={() => navigate('/pricing')}
                  data-testid="upgrade-btn"
                  style={{ background: '#2563EB', color: '#fff', border: 'none',
                           padding: '10px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700,
                           cursor: 'pointer' }}
                >Upgrade to Pro →</button>
              ) : (
                <button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  data-testid="manage-subscription-btn"
                  style={{ background: '#fff', color: '#0F2742', border: 'none',
                           padding: '10px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700,
                           cursor: portalLoading ? 'wait' : 'pointer',
                           opacity: portalLoading ? 0.6 : 1 }}
                >{portalLoading ? 'Opening…' : 'Manage subscription'}</button>
              )}
            </div>
            {billing.pending_tier_change && (
              <div style={{ width: '100%', fontSize: 12, opacity: 0.9, marginTop: 4 }}
                   data-testid="pending-change-notice">
                {billing.pending_tier_change.to === 'free'
                  ? 'Your subscription will end at the end of this billing period.'
                  : `Downgrade to ${billing.pending_tier_change.to === 'pro' ? 'Pro' : 'Free'} scheduled for the end of this billing period.`}
              </div>
            )}
            {billing.payment_grace_until && (
              <div style={{ width: '100%', fontSize: 12, background: '#FEF3C7', color: '#92400E',
                            padding: '8px 10px', borderRadius: 8, marginTop: 8 }}
                   data-testid="grace-period-notice">
                ⚠ Your last payment failed. Please update your payment method before {new Date(billing.payment_grace_until).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} to keep your subscription.
              </div>
            )}
          </div>
        )}

        {/* Pro+ QS Consultation pill — direct redeem from portal (no need to go via /scope/draw) */}
        {billing && billing.tier === 'pro_plus' && (() => {
          const remaining = billing.monthly_allowances?.quarterly_calls_remaining
            ?? billing.monthly_allowances?.qs_calls_remaining ?? 0;
          const cap = billing.monthly_allowances?.quarterly_calls_cap
            ?? billing.monthly_allowances?.qs_calls_cap ?? 0;
          const hasAllowance = remaining > 0;
          return (
            <div
              data-testid="qs-consultation-pill"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 14, flexWrap: 'wrap',
                padding: '14px 18px',
                background: hasAllowance ? 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)' : '#F8FAFC',
                border: `1.5px solid ${hasAllowance ? '#2563EB' : '#E2E8F0'}`,
                borderRadius: 10, marginBottom: 18,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 9,
                  background: hasAllowance ? '#2563EB' : '#94A3B8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Calendar size={18} style={{ color: '#fff' }} />
                </div>
                <div style={{ fontSize: 13, color: '#0F2742', lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 800, marginBottom: 2 }}>Project Cost Review — 30 minutes</div>
                  <div style={{ color: hasAllowance ? '#1D4ED8' : '#64748B' }} data-testid="qs-consultation-remaining">
                    {hasAllowance
                      ? <><strong>{remaining} of {cap}</strong> consultations remaining this year</>
                      : <>You've used all {cap} consultations this year — next reset on your annual renewal</>}
                  </div>
                </div>
              </div>
              <button
                onClick={hasAllowance ? handleRedeemQsConsultation : () => navigate('/scope/draw')}
                disabled={qsRedeemLoading}
                data-testid="qs-consultation-redeem-btn"
                style={{
                  background: hasAllowance ? '#2563EB' : '#0F2742',
                  color: '#fff', border: 'none',
                  padding: '10px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700,
                  cursor: qsRedeemLoading ? 'wait' : 'pointer',
                  opacity: qsRedeemLoading ? 0.6 : 1, whiteSpace: 'nowrap',
                }}
              >
                {qsRedeemLoading
                  ? 'Opening…'
                  : hasAllowance ? 'Book your Project Cost Review →' : 'Book at £199 →'}
              </button>
            </div>
          );
        })()}

        {/* Session summary + SCOPE drafts (Phase B) */}
        {sessionInfo && (
          <div
            data-testid="session-summary"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 14, flexWrap: 'wrap',
              padding: '14px 18px', background: sessionInfo.count >= sessionInfo.limit ? '#FEF3C7' : '#F8FAFC',
              border: `1.5px solid ${sessionInfo.count >= sessionInfo.limit ? '#FCD34D' : '#E2E8F0'}`,
              borderRadius: 10, marginBottom: 18,
            }}
          >
            <div style={{ fontSize: 13, color: '#0F2742' }}>
              <strong data-testid="session-count">{sessionInfo.count} of {sessionInfo.limit}</strong> saved projects · {sessionInfo.tier === 'pro_plus' ? 'Pro+' : (sessionInfo.tier.charAt(0).toUpperCase() + sessionInfo.tier.slice(1))}
              {sessionInfo.count >= sessionInfo.limit ? ' — limit reached' : ''}
            </div>
            <button
              onClick={() => navigate('/scope')}
              style={{ background: '#0F2742', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              data-testid="start-scope-btn"
            >Start a new SCOPE →</button>
          </div>
        )}

        {drafts.length > 0 && (
          <div data-testid="drafts-list" style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10 }}>In progress</div>
            {drafts.map((d) => (
              <div key={d.draft_id} className="cp-order" style={{ marginBottom: 10 }}>
                <div className="cp-order-hdr" style={{ cursor: 'default' }}>
                  <div className="cp-order-icon">📝</div>
                  <div className="cp-order-main">
                    <div className="cp-order-product">SCOPE draft · in progress</div>
                    <div className="cp-order-address">{d.address || '(no address yet)'}</div>
                    <div className="cp-order-meta" style={{ fontSize: 11, color: '#94A3B8' }}>
                      {(d.rooms || []).length} room{(d.rooms || []).length === 1 ? '' : 's'} · Last edited {new Date(d.updated_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleContinueDraft(d.draft_id)}
                      style={{ background: '#0F2742', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                      data-testid={`continue-draft-${d.draft_id.slice(0, 6)}`}
                    >Continue →</button>
                    <button
                      onClick={() => handleArchiveDraft(d.draft_id)}
                      style={{ background: '#fff', color: '#64748B', border: '1.5px solid #E2E8F0', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      data-testid={`archive-draft-${d.draft_id.slice(0, 6)}`}
                    >Archive</button>
                    <button
                      onClick={() => handleDeleteDraft(d.draft_id)}
                      style={{ background: '#fff', color: '#991B1B', border: '1.5px solid #FECACA', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      data-testid={`delete-draft-${d.draft_id.slice(0, 6)}`}
                    >Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Orders */}
        {orders.length === 0 && drafts.length === 0 ? (
          <div className="cp-empty" data-testid="no-orders">
            <p>No orders found for this email address.</p>
            <button className="cp-btn cp-btn-accent" onClick={() => navigate('/estimate')}>
              Get a cost estimate <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="cp-orders" data-testid="orders-list">
            <div className="cp-orders-label">{orders.length} order{orders.length !== 1 ? 's' : ''}</div>
            {orders.map((order, idx) => {
              const isExpanded = expandedOrder === idx;
              const productLabel = PRODUCT_LABELS[order.product_type] || order.product_type;
              const productIcon = PRODUCT_ICONS[order.product_type] || '📄';
              const hasReports = order.report_files && Object.keys(order.report_files).length > 0;
              const isQS = order.product_type === 'qs_check';
              const qsStatus = order.qs_status;
              const isExpired = order.expired;
              const isPaid = order.status === 'paid' || order.status === 'delivered';
              const isIndicative = order.product_type === 'indicative';
              const isDetailed = order.product_type === 'detailed';
              const showQSUpsell = (isIndicative || isDetailed) && !order.has_qs_check && isPaid;
              const showDetailedUpsell = isIndicative && isPaid;

              return (
                <div key={order.project_id || order.order_ref || idx} className={`cp-order${isExpanded ? ' expanded' : ''}`} data-testid={`order-${idx}`}>
                  {/* Order header */}
                  <div className="cp-order-hdr" onClick={() => setExpandedOrder(isExpanded ? null : idx)}>
                    <div className="cp-order-hdr-left">
                      <span className="cp-order-icon">{productIcon}</span>
                      <div>
                        <div className="cp-order-product">{productLabel}</div>
                        <div className="cp-order-addr">{order.address || order.address_full || '—'}</div>
                        <div className="cp-order-meta-row">
                          <span className="cp-order-ref">{order.order_ref || '—'}</span>
                          {order.scope_level && <span className="cp-order-scope">{order.scope_level}</span>}
                          {order.output_range_low > 0 && order.output_range_high > 0 && (
                            <span className="cp-order-range">£{Math.round(order.output_range_low / 1000)}k – £{Math.round(order.output_range_high / 1000)}k</span>
                          )}
                          {order.epc_area && <span className="cp-order-area">{order.epc_area}m²</span>}
                        </div>
                      </div>
                    </div>
                    <div className="cp-order-hdr-right">
                      {isPaid && !isExpired && <span className="cp-badge paid">Paid</span>}
                      {isExpired && <span className="cp-badge expired">Expired</span>}
                      {isQS && qsStatus && (
                        <span className="cp-badge" style={{ background: QS_STATUS_STYLES[qsStatus]?.bg, color: QS_STATUS_STYLES[qsStatus]?.text }}>
                          {qsStatus}
                        </span>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                    </div>
                  </div>

                  {/* Order body */}
                  {isExpanded && (
                    <div className="cp-order-body">
                      {/* Expiry date */}
                      {order.expiry_date && !isExpired && (
                        <div className="cp-expiry-line">
                          <Calendar className="w-3.5 h-3.5" /> Available until <strong>{order.expiry_date}</strong>
                        </div>
                      )}
                      {isExpired && (
                        <div className="cp-expiry-line expired">
                          <Calendar className="w-3.5 h-3.5" /> Expired on {order.expiry_date}
                        </div>
                      )}

                      {/* Downloads */}
                      {!isExpired && hasReports && (
                        <div className="cp-downloads">
                          <div className="cp-dl-label">Documents</div>
                          {Object.keys(order.report_files).map(type => (
                            <button key={type} className="cp-dl-btn" onClick={() => handleDownload(order.order_ref, type)} data-testid={`dl-${order.order_ref}-${type}`}>
                              <Download className="w-3.5 h-3.5" />
                              {type === 'sow' ? 'Schedule of Works' : type === 'breakdown' ? 'Cost Estimate' : type === 'materials' ? 'Materials List' : type === 'guide' ? 'What To Do Next' : type}
                            </button>
                          ))}
                          {/* Share button — scope_full / scope_sow / detailed only */}
                          {['scope_full', 'scope_sow', 'detailed'].includes(order.product_type) && (
                            <button
                              className="cp-dl-btn"
                              onClick={() => handleShareSow(order.order_ref)}
                              data-testid={`share-${order.order_ref}`}
                              style={{ background: '#EFF6FF', color: '#1D4ED8', borderColor: '#BFDBFE' }}
                            >
                              <Share2 className="w-3.5 h-3.5" /> Share link (30-day)
                            </button>
                          )}
                        </div>
                      )}

                      {/* QS Check report download */}
                      {isQS && order.qs_delivered && order.qs_report_s3_key && !isExpired && (
                        <div className="cp-downloads">
                          <div className="cp-dl-label">QS-Verified Report</div>
                          <button className="cp-dl-btn accent" onClick={() => handleDownload(order.order_ref, 'qs_report')} data-testid={`dl-qs-${order.order_ref}`}>
                            <Download className="w-3.5 h-3.5" /> Download QS Report
                          </button>
                          {order.qs_surveyor_notes && (
                            <div className="cp-surveyor-notes">
                              <div className="cp-sn-label">Note from your surveyor</div>
                              <div className="cp-sn-text">{order.qs_surveyor_notes}</div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* QS Check status tracker */}
                      {isQS && !order.qs_delivered && (
                        <div className="cp-qs-tracker">
                          <div className="cp-qs-step done"><Check className="w-3 h-3" /> Ordered</div>
                          <div className={`cp-qs-step${qsStatus === 'In Review' || qsStatus === 'Report Ready' ? ' done' : ''}`}>
                            {qsStatus === 'In Review' || qsStatus === 'Report Ready' ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                            In Review
                          </div>
                          <div className={`cp-qs-step${qsStatus === 'Report Ready' ? ' done' : ''}`}>
                            {qsStatus === 'Report Ready' ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                            Report Ready
                          </div>
                        </div>
                      )}

                      {/* Expired: get updated estimate */}
                      {isExpired && (
                        <button className="cp-btn cp-btn-navy" onClick={() => navigate(isDetailed ? '/scope' : '/estimate')} data-testid="get-updated-btn">
                          Get an updated estimate <ArrowRight className="w-4 h-4" />
                        </button>
                      )}

                      {/* Upsell: QS Check */}
                      {showQSUpsell && (
                        <div className="cp-upsell" data-testid={`upsell-qs-${idx}`}>
                          <div className="cp-upsell-left">
                            <Search className="w-3.5 h-3.5" />
                            <span>Want total confidence? <strong>Add QS Check — <QSPriceBadge variant="inline" /></strong></span>
                          </div>
                          <button className="cp-upsell-btn" onClick={() => handleAddQSCheck(order)}>Add →</button>
                        </div>
                      )}

                      {/* Upsell: Detailed */}
                      {showDetailedUpsell && (
                        <div className="cp-upsell" data-testid={`upsell-detailed-${idx}`}>
                          <div className="cp-upsell-left">
                            <Ruler className="w-3.5 h-3.5" />
                            <span>Need precise quantities? <strong>Upgrade to Detailed — £199</strong></span>
                          </div>
                          <button className="cp-upsell-btn" onClick={() => navigate('/scope')}>Upgrade →</button>
                        </div>
                      )}

                      {/* Archive + Delete action row — user self-service */}
                      {order.project_id && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 14, marginTop: 10 }}>
                          {isPaid && ['detailed', 'scope_materials', 'scope_sow', 'scope_full'].includes(order.product_type) && !order.archived && (
                            <button
                              onClick={() => handleArchiveProject(order.project_id)}
                              style={{ background: 'transparent', color: '#94A3B8', border: 'none', fontSize: 11, padding: 6, cursor: 'pointer', textDecoration: 'underline' }}
                              data-testid={`archive-project-${idx}`}
                            >
                              Archive (keeps access, frees a slot)
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteProject(order.project_id, order.address || order.address_full)}
                            style={{ background: 'transparent', color: '#B91C1C', border: 'none', fontSize: 11, padding: 6, cursor: 'pointer', textDecoration: 'underline' }}
                            data-testid={`delete-project-${idx}`}
                            title="Permanently delete this project from your account"
                          >
                            Delete project
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* QS Consultation Call — persistent upsell (Phase D) */}
        <div style={{ marginTop: 28, marginBottom: 18 }} data-testid="portal-qs-call-wrap">
          <QSCallCTA variant="card" showRedeem testid="qs-call-cta-portal" />
        </div>

        {/* Marketing preference */}
        <div className="cp-consent-card" data-testid="marketing-toggle">
          <div className="cp-consent-row">
            <div>
              <div className="cp-consent-title">Email preferences</div>
              <div className="cp-consent-desc">Help me get the best value on my project — send me contractor rate insights, cost-saving tips and market updates relevant to my refurbishment</div>
            </div>
            <label className="cp-switch">
              <input type="checkbox" checked={marketingConsent} onChange={handleToggleConsent} data-testid="marketing-toggle-input" />
              <span className="cp-slider" />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
